import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, Typography } from "@mui/material";
import { http } from "../lib/axios";

function parseJwtExpMs(token) {
  try {
    const base64 = token.split(".")[1];
    const decoded = JSON.parse(atob(base64.replace(/-/g, "+").replace(/_/g, "/")));
    if (!decoded?.exp) return null;
    return decoded.exp * 1000;
  } catch {
    return null;
  }
}

function getTokens() {
  return {
    access: localStorage.getItem("access_token") || null,
    refresh: localStorage.getItem("refresh_token") || null,
    // NEW: support opaque tokens (DOT) using a stored expiry
    accessExpiresAt: Number(localStorage.getItem("access_expires_at") || 0) || null,
  };
}

function setTokens({ access, refresh, access_expires_at }) {
  if (access != null) localStorage.setItem("access_token", access);
  if (refresh != null) localStorage.setItem("refresh_token", refresh);
  if (typeof access_expires_at === "number") {
    localStorage.setItem("access_expires_at", String(access_expires_at));
  }
}

function clearTokensAndRole() {
  localStorage.removeItem("access_token");
  localStorage.removeItem("refresh_token");
  localStorage.removeItem("access_expires_at");
  localStorage.removeItem("user_role");
}

async function refreshTokenDOT() {
  const refresh = localStorage.getItem("refresh_token");
  if (!refresh) throw new Error("No refresh token available");

  // ✅ CRA uses process.env.REACT_APP_*
  const client_id = process.env.REACT_APP_OAUTH_CLIENT_ID;
  const client_secret = process.env.REACT_APP_OAUTH_CLIENT_SECRET; // optional

  if (!client_id) {
    throw new Error("Missing REACT_APP_OAUTH_CLIENT_ID (set it in your .env)");
  }

  const form = new URLSearchParams();
  form.set("grant_type", "refresh_token");
  form.set("refresh_token", refresh);
  form.set("client_id", client_id);
  if (client_secret) form.set("client_secret", client_secret);

  // Important: skip auth so no stale Authorization header is attached
  const { data } = await http.post("/o/token/", form, {
    headers: { "Content-Type": "application/x-www-form-urlencoded" },
    skipAuth: true,
    withCredentials: false,
  });

  const access = data.access_token || data.access;
  const newRefresh = data.refresh_token || refresh;
  const expiresIn = Number(data.expires_in || 0); // seconds
  const access_expires_at = expiresIn ? Date.now() + expiresIn * 1000 : null;

  return { access, refresh: newRefresh, access_expires_at };
}

export default function SessionProvider({ children, warnMs = 2 * 60 * 1000 }) {
  const navigate = useNavigate();
  const [showWarning, setShowWarning] = useState(false);
  const logoutTimerRef = useRef(null);
  const warnTimerRef = useRef(null);
  const [expAt, setExpAt] = useState(null);

  const doLogout = useCallback(() => {
    clearTokensAndRole();
    setShowWarning(false);
    navigate("/login");
  }, [navigate]);

  const scheduleTimers = useCallback((accessToken) => {
    // clear old
    if (logoutTimerRef.current) clearTimeout(logoutTimerRef.current);
    if (warnTimerRef.current) clearTimeout(warnTimerRef.current);

    // Try JWT exp first
    let expMs = accessToken ? parseJwtExpMs(accessToken) : null;

    if (!expMs) {
      // Fallback to stored expires_at (opaque tokens / DOT)
      const { accessExpiresAt } = getTokens();
      if (accessExpiresAt && accessExpiresAt > Date.now()) {
        expMs = accessExpiresAt;
      }
    }

    // If we STILL can't determine expiry, do not force-logout.
    if (!expMs) {
      setExpAt(null);
      setShowWarning(false);
      return;
    }

    setExpAt(expMs);
    if (expMs <= Date.now()) {
      doLogout();
      return;
    }

    const msToLogout = expMs - Date.now();
    const msToWarn = Math.max(msToLogout - warnMs, 0);

    warnTimerRef.current = setTimeout(() => setShowWarning(true), msToWarn);
    logoutTimerRef.current = setTimeout(() => doLogout(), msToLogout + 1000);
  }, [doLogout, warnMs]);

  const doStayLoggedIn = useCallback(async () => {
    try {
      const tokens = await refreshTokenDOT();
      setTokens(tokens);
      setShowWarning(false);
      if (tokens.access) scheduleTimers(tokens.access);
    } catch (err) {
      console.error("Token refresh failed:", err);
      doLogout();
    }
  }, [doLogout, scheduleTimers]);

  useEffect(() => {
    const { access } = getTokens();
    if (access) scheduleTimers(access);
  }, [scheduleTimers]);

  useEffect(() => {
    return () => {
      if (logoutTimerRef.current) clearTimeout(logoutTimerRef.current);
      if (warnTimerRef.current) clearTimeout(warnTimerRef.current);
    };
  }, []);

  const minutesLeft = useMemo(() => {
    if (!expAt) return null;
    const ms = expAt - Date.now();
    return ms > 0 ? Math.ceil(ms / 60000) : 0;
  }, [expAt]);

  return (
    <>
      {children}
      <Dialog open={showWarning} onClose={() => setShowWarning(false)}>
        <DialogTitle>Session expiring soon</DialogTitle>
        <DialogContent dividers>
          <Typography gutterBottom>
            Your session is about to time out{typeof minutesLeft === "number" ? ` (in ~${minutesLeft} minute${minutesLeft === 1 ? "" : "s"})` : ""}.
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Stay logged in to continue your work. Otherwise you’ll be signed out automatically.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={doLogout} color="error">Log out</Button>
          <Button onClick={doStayLoggedIn} variant="contained">Stay logged in</Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
